"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useAppContext } from "./AppContext";
import { useIsTabletPortrait } from "@/hooks/useIsTabletPortrait";
import { useLayoutMode } from "@/hooks/useLayoutMode";
import { useLibraryContext } from "./LibraryContext";
import { useStappenplanContext } from "./StappenplanContext";
import { useTheoryContext } from "./TheoryContext";
import { useParentAudience } from "@/hooks/useParentAudience";
import { useAuthContext } from "./AuthContext";
import posthog from "posthog-js";
import {
  getEffectiveTourStatus,
  isDismissedTourStatus,
  readLocalTourStatus,
  type TourStatus,
  writeLocalTourStatus
} from "@/lib/tourState";
import { setFeatureDiscoveryTourSuppressed } from "@/lib/featureDiscoveryTourGuard";
import { getFirstVisibleDesktopTheoryStepIndex } from "@/containers/library/vraagDisplay/NewStappenplan/utils";

/**
 * Guided tour steps (semantic IDs — some may be skipped based on content):
 * 0 = Intro (inline welcome card, no overlay)
 * 1 = Lesboeken (demo: explain book alignment; lesboek: open the book)
 * 2 = Context (demo: introduce the question)
 * 3 = Hint (hint visible, explain didactic idea, cursor on "Begin stappenplan")
 * 4 = Stappenplan (cursor on "Volgende stap", then poll for theorie pill)
 * 5 = Theoriepill (cursor on theory pill, then "Volgende stap" after viewing)
 * 6 = Bijlesbot (cursor on chat input, send example question)
 * 7 = Outro (completion card)
 */
export type TourStep = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

const OPGAVE_IN_BOEK_NOTICE_STORAGE_KEY =
  "wiswiz_opgave_in_boek_notice_dismissed";
const OPGAVE_IN_BOEK_NOTICE_PREFERENCE_KEY =
  "opgave_in_boek_notice_v1_dismissed";

const LEGACY_ALL_STEPS: TourStep[] = [0, 1, 2, 3, 4, 5, 6];
const TIKTOK_ALL_STEPS: TourStep[] = [0, 1, 2, 3, 4, 5, 6, 7];

const LEGACY_STEP_NAME: Record<TourStep, string> = {
  0: "intro",
  1: "question_intro",
  2: "hint",
  3: "stappenplan",
  4: "theorie",
  5: "bijlesbot",
  6: "outro",
  7: "outro"
};

const TIKTOK_STEP_NAME: Record<TourStep, string> = {
  0: "intro",
  1: "lesboeken",
  2: "question_intro",
  3: "hint",
  4: "stappenplan",
  5: "theorie",
  6: "bijlesbot",
  7: "outro"
};

export const TOUR_RETURN_TO_OUTRO_KEY = "wiswiz_guided_tour_return_to_outro";
export const TOUR_RETURN_SNAPSHOT_KEY = "wiswiz_guided_tour_return_snapshot";
export const TOUR_RETURN_HISTORY_TOKEN_KEY = "wiswizGuidedTourReturnToken";
const TIKTOK_TOUR_STEP_PARAM = "tour_step";
const TIKTOK_TOUR_TARGET_PARAM = "tour_target";
const TIKTOK_TOUR_SNAPSHOTS_KEY = "wiswiz_tiktok_tour_snapshots";
export const TIKTOK_TOUR_SUPPRESS_HISTORY_STEP_KEY =
  "wiswiz_tiktok_tour_suppress_history_step";

const MAX_THEORY_BRIDGE_EXTRA_CLICKS = 1;

/** Cursor target for each step — shown simultaneously with the tip */
const LEGACY_STEP_CURSOR: Partial<Record<TourStep, string>> = {
  1: "toon-hint",
  2: "begin-stappenplan",
  3: "volgende-stap",
  4: "theorie-pill",
  5: "volgende-stap"
};

const TIKTOK_STEP_CURSOR: Partial<Record<TourStep, string>> = {
  1: "bekijk-voorbeeldvraag",
  2: "toon-hint",
  3: "begin-stappenplan",
  4: "volgende-stap",
  5: "theorie-inline",
  6: "volgende-stap"
};

type GuidedOnboardingContextType = {
  currentTourStep: TourStep | null;
  cursorTarget: string | null;
  theoryBridgeRemainingClicks: number | null;
  skippedSteps: Set<TourStep>;
  advanceTour: () => void;
  completeTourBeforeSubscribe: () => void;
  skipTour: () => void;
  restartGuided: () => void;
  isTourRunning: boolean;
  isTourComplete: boolean;
  isDemo: boolean;
};

type TourReturnSnapshot = {
  token?: string;
  pathname?: string;
  currentStepIndex?: number;
  showHint?: boolean;
  showInitialMenu?: boolean;
  showBegripvraag?: boolean;
  isZelfUitwerkenMode?: boolean;
  showInitialMobileMenu?: boolean;
  sideMenuExpanded?: boolean;
};

type TikTokTourSnapshot = TourReturnSnapshot & {
  step: TourStep;
  cursorTarget: string | null;
  inlineTheorieId?: number;
};

const GuidedOnboardingContext =
  createContext<GuidedOnboardingContextType | null>(null);

function clearTourReturnState() {
  try {
    sessionStorage.removeItem(TOUR_RETURN_TO_OUTRO_KEY);
    sessionStorage.removeItem(TOUR_RETURN_SNAPSHOT_KEY);
  } catch {}
  try {
    const state = window.history.state;
    if (
      !state ||
      typeof state !== "object" ||
      !(TOUR_RETURN_HISTORY_TOKEN_KEY in state)
    ) {
      return;
    }
    const nextState = { ...state };
    delete nextState[TOUR_RETURN_HISTORY_TOKEN_KEY];
    window.history.replaceState(nextState, "", window.location.href);
  } catch {}
}

function createTourReturnToken(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random()}`;
  }
}

function getTourReturnHistoryToken(): string | undefined {
  try {
    const token = window.history.state?.[TOUR_RETURN_HISTORY_TOKEN_KEY];
    return typeof token === "string" ? token : undefined;
  } catch {
    return undefined;
  }
}

function setTourReturnHistoryToken(token: string) {
  try {
    const state = window.history.state;
    const nextState =
      state && typeof state === "object"
        ? { ...state, [TOUR_RETURN_HISTORY_TOKEN_KEY]: token }
        : { [TOUR_RETURN_HISTORY_TOKEN_KEY]: token };
    window.history.replaceState(nextState, "", window.location.href);
  } catch {}
}

function parseTourStep(value: string | null | undefined): TourStep | null {
  if (value === null || value === undefined || value.trim() === "") {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) return null;
  if (parsed < 0 || parsed > 7) return null;
  return parsed as TourStep;
}

function getTikTokTourEntryKey(
  pathname: string | null,
  searchParams: URLSearchParams
): string {
  const params = new URLSearchParams(searchParams.toString());
  params.delete(TIKTOK_TOUR_STEP_PARAM);
  params.delete(TIKTOK_TOUR_TARGET_PARAM);
  const query = params.toString();
  return `${pathname ?? ""}${query ? `?${query}` : ""}`;
}

function getTikTokTourHistoryPath(
  pathname: string | null,
  searchParams: URLSearchParams,
  step: TourStep,
  target: string | null
): string {
  const params = new URLSearchParams(searchParams.toString());
  params.set(TIKTOK_TOUR_STEP_PARAM, String(step));
  if (target) {
    params.set(TIKTOK_TOUR_TARGET_PARAM, target);
  } else {
    params.delete(TIKTOK_TOUR_TARGET_PARAM);
  }
  const query = params.toString();
  return `${pathname ?? ""}${query ? `?${query}` : ""}`;
}

function readTikTokTourSnapshot(key: string): TikTokTourSnapshot | null {
  try {
    const raw = sessionStorage.getItem(TIKTOK_TOUR_SNAPSHOTS_KEY);
    if (!raw) return null;
    const snapshots = JSON.parse(raw) as Record<string, TikTokTourSnapshot>;
    return snapshots[key] ?? null;
  } catch {
    return null;
  }
}

function writeTikTokTourSnapshot(key: string, snapshot: TikTokTourSnapshot) {
  try {
    const raw = sessionStorage.getItem(TIKTOK_TOUR_SNAPSHOTS_KEY);
    const snapshots = raw
      ? (JSON.parse(raw) as Record<string, TikTokTourSnapshot>)
      : {};
    snapshots[key] = snapshot;
    sessionStorage.setItem(
      TIKTOK_TOUR_SNAPSHOTS_KEY,
      JSON.stringify(snapshots)
    );
  } catch {}
}

export function GuidedOnboardingProvider({ children }: PropsWithChildren) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { api, isMobile } = useAppContext();
  const isTabletPortrait = useIsTabletPortrait();
  const { layoutMode } = useLayoutMode();
  const isOnVraagPage = pathname?.startsWith("/library/vraag/") ?? false;
  const isDemo = pathname?.startsWith("/library/vraag/demo") ?? false;
  const isTikTokTour =
    searchParams?.get("tour") === "tiktok" && isMobile === true;
  const isForcedTikTokTour = isDemo && isTikTokTour;
  const searchParamsString = searchParams?.toString() ?? "";
  const searchParamsForHelpers = useMemo(
    () => new URLSearchParams(searchParamsString),
    [searchParamsString]
  );
  const urlTikTokTourStep = isForcedTikTokTour
    ? parseTourStep(searchParams?.get(TIKTOK_TOUR_STEP_PARAM))
    : null;
  const urlTikTokTourTarget =
    isForcedTikTokTour && searchParams?.get(TIKTOK_TOUR_TARGET_PARAM)?.trim()
      ? searchParams.get(TIKTOK_TOUR_TARGET_PARAM)
      : null;

  const {
    showGuidedExperience,
    setShowGuidedExperience,
    sideMenuExpanded,
    setSideMenuExpanded,
    currentSubvraag,
    currentStappenplan,
    shareToken
  } = useLibraryContext();

  const {
    setShowHint,
    setShowInitialMenu,
    setCurrentStepIndex,
    showHint,
    currentStepIndex,
    setShowBegripvraag,
    showBegripvraag,
    isZelfUitwerkenMode,
    setIsZelfUitwerkenMode,
    showInitialMenu,
    showInitialMobileMenu,
    setShowInitialMobileMenu,
    stepMapping,
    startStappenplan
  } = useStappenplanContext();

  const {
    closeTheory,
    showTheory,
    inlineTheorie,
    closeInlineTheorie,
    handleInlineTheorieClick
  } = useTheoryContext();
  const {
    isLoggedIn,
    userData,
    setTourStatusOnUserData,
    setUserPreferencesOnUserData
  } = useAuthContext();

  const isParentAudience = useParentAudience();
  const flow = isDemo ? "demo" : "lesboek";
  const allSteps = isTikTokTour ? TIKTOK_ALL_STEPS : LEGACY_ALL_STEPS;
  const stepName = isTikTokTour ? TIKTOK_STEP_NAME : LEGACY_STEP_NAME;
  const stepCursor = isTikTokTour ? TIKTOK_STEP_CURSOR : LEGACY_STEP_CURSOR;
  const outroStep: TourStep = isTikTokTour ? 7 : 6;
  const hintStep: TourStep = isTikTokTour ? 3 : 2;
  const stappenplanStep: TourStep = isTikTokTour ? 4 : 3;
  const theoryStep: TourStep = isTikTokTour ? 5 : 4;
  const theoryTarget = isTikTokTour ? "theorie-inline" : "theorie-pill";
  const theoryIsOpen = isTikTokTour ? !!inlineTheorie : !!showTheory;

  const [currentTourStep, setCurrentTourStep] = useState<TourStep | null>(() =>
    isForcedTikTokTour ? (urlTikTokTourStep ?? 0) : null
  );
  const [cursorTarget, setCursorTarget] = useState<string | null>(
    urlTikTokTourTarget
  );
  const [isTourRunning, setIsTourRunning] = useState(isForcedTikTokTour);
  const [isTourComplete, setIsTourComplete] = useState(() =>
    isForcedTikTokTour ? false : isDismissedTourStatus(readLocalTourStatus())
  );
  const [stepIndexAtTheory, setStepIndexAtTheory] = useState<number | null>(
    null
  );
  const [theoryBridgeRemainingClicks, setTheoryBridgeRemainingClicks] =
    useState<number | null>(null);
  // Prevent tour from restarting after explicit skip within same page visit
  const tourSkippedRef = useRef(false);
  const tourStartTimeRef = useRef<number | null>(null);
  const lastTrackedStepRef = useRef<TourStep | null>(null);
  const stepEnteredAtRef = useRef<number | null>(null);
  const previousStepRef = useRef<TourStep | null>(null);
  const tourCompletionCapturedRef = useRef(false);
  const restoredOutroRef = useRef(false);
  const subscribeReturnPendingRef = useRef(false);
  const forcedTikTokTourEntryRef = useRef<string | null>(null);
  const lastAppliedTikTokTourUrlRef = useRef<string | null>(null);
  const selfPushedTikTokTourUrlRef = useRef<string | null>(null);
  const isRestoringTikTokTourUrlRef = useRef(false);
  const restoredTikTokTourStepRef = useRef<TourStep | null>(null);
  const stepIndexAtStap3Ref = useRef<number | null>(null);
  const delayedCursorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    return () => {
      if (delayedCursorTimerRef.current) {
        clearTimeout(delayedCursorTimerRef.current);
      }
      setFeatureDiscoveryTourSuppressed(false);
    };
  }, []);

  useEffect(() => {
    setFeatureDiscoveryTourSuppressed(isTourRunning);
    return () => setFeatureDiscoveryTourSuppressed(false);
  }, [isTourRunning]);

  // ─── Content-aware step skipping ───

  const hasMultipleSteps = stepMapping.length > 1;
  const firstVisibleDesktopTheoryStepIndex = useMemo(() => {
    if (isTikTokTour || !currentStappenplan) return null;
    return getFirstVisibleDesktopTheoryStepIndex(
      currentStappenplan,
      stepMapping
    );
  }, [currentStappenplan, isTikTokTour, stepMapping]);

  const skippedSteps = useMemo(() => {
    const skip = new Set<TourStep>();

    if (isTikTokTour) {
      // The example-question reveal is demo-only. Real textbook tours go from
      // the book-reference step directly to the hint step.
      if (!isDemo) skip.add(2);

      // Step 3 (hint): skip if no hint text
      if (!currentSubvraag?.hint?.tekst) skip.add(3);
    } else {
      // Legacy desktop/default tour semantics from develop.
      if (!currentSubvraag?.hint?.tekst) skip.add(2);

      // On regular mobile demo, step 0 is the only guided moment.
      if (isMobile && isDemo) skip.add(2);
    }

    // Steps 3 and 4 depend on stappenplan data — only decide once loaded
    if (currentStappenplan) {
      // With only 1 step, there's no "Volgende stap" button — skip both
      // step 4 (needs the button) and step 5 (needs it for the 5→6 transition)
      if (!hasMultipleSteps) {
        skip.add(isTikTokTour ? 4 : 3);
        skip.add(isTikTokTour ? 5 : 4);
      }

      // Theory step: skip if no theories available at all
      if (!currentStappenplan.theorieen?.length) {
        skip.add(isTikTokTour ? 5 : 4);
      }

      if (!isTikTokTour) {
        if (firstVisibleDesktopTheoryStepIndex === null) {
          skip.add(4);
        } else {
          const extraClicksFromFirstTheoryCheck =
            firstVisibleDesktopTheoryStepIndex - 1;
          if (
            firstVisibleDesktopTheoryStepIndex < 1 ||
            extraClicksFromFirstTheoryCheck > MAX_THEORY_BRIDGE_EXTRA_CLICKS
          ) {
            skip.add(4);
          }
        }
      }
    }

    return skip;
  }, [
    firstVisibleDesktopTheoryStepIndex,
    isDemo,
    isMobile,
    isTikTokTour,
    currentSubvraag,
    currentStappenplan,
    hasMultipleSteps
  ]);

  /** Find the next non-skipped step after `from` */
  const nextActiveStep = useCallback(
    (from: TourStep): TourStep => {
      const fromIdx = allSteps.indexOf(from);
      for (let i = fromIdx + 1; i < allSteps.length; i++) {
        if (!skippedSteps.has(allSteps[i])) return allSteps[i];
      }
      return outroStep; // fallback to outro
    },
    [allSteps, outroStep, skippedSteps]
  );

  const getCursorForStep = useCallback(
    (step: TourStep): string | null => {
      if (step === 1 && !isDemo) return "tour-primary-action";
      return stepCursor[step] || null;
    },
    [isDemo, stepCursor]
  );

  const forcedTikTokTourKey = isForcedTikTokTour
    ? getTikTokTourEntryKey(pathname, searchParamsForHelpers)
    : null;
  const urlTikTokTourHistoryPath =
    isForcedTikTokTour && urlTikTokTourStep !== null
      ? getTikTokTourHistoryPath(
          pathname,
          searchParamsForHelpers,
          urlTikTokTourStep,
          urlTikTokTourTarget
        )
      : null;

  useEffect(() => {
    if (!isForcedTikTokTour) {
      forcedTikTokTourEntryRef.current = null;
      lastAppliedTikTokTourUrlRef.current = null;
      selfPushedTikTokTourUrlRef.current = null;
      isRestoringTikTokTourUrlRef.current = false;
      restoredTikTokTourStepRef.current = null;
      return;
    }
    const entryChanged =
      forcedTikTokTourEntryRef.current !== forcedTikTokTourKey;
    const urlStateChanged =
      !!urlTikTokTourHistoryPath &&
      lastAppliedTikTokTourUrlRef.current !== urlTikTokTourHistoryPath;
    const isSelfPushedUrl =
      !!urlTikTokTourHistoryPath &&
      selfPushedTikTokTourUrlRef.current === urlTikTokTourHistoryPath;
    if (!entryChanged && isSelfPushedUrl) {
      selfPushedTikTokTourUrlRef.current = null;
      lastAppliedTikTokTourUrlRef.current = urlTikTokTourHistoryPath;
      return;
    }
    if (!entryChanged && !urlStateChanged) return;

    const step = urlTikTokTourStep ?? 0;
    const target = urlTikTokTourTarget ?? getCursorForStep(step);
    const snapshot = urlTikTokTourHistoryPath
      ? readTikTokTourSnapshot(urlTikTokTourHistoryPath)
      : null;
    const restoredStep = snapshot?.step ?? step;
    const isBrowserHistoryRestore = urlStateChanged && !isSelfPushedUrl;

    forcedTikTokTourEntryRef.current = forcedTikTokTourKey;
    if (isSelfPushedUrl) {
      selfPushedTikTokTourUrlRef.current = null;
    }
    isRestoringTikTokTourUrlRef.current = isBrowserHistoryRestore;
    restoredTikTokTourStepRef.current = isBrowserHistoryRestore
      ? restoredStep
      : null;
    tourSkippedRef.current = false;
    tourCompletionCapturedRef.current = false;
    restoredOutroRef.current = false;
    subscribeReturnPendingRef.current = false;

    if (snapshot) {
      const showHintForStep =
        snapshot.step <= hintStep ? true : (snapshot.showHint ?? false);
      setShowHint(showHintForStep);
      setShowInitialMenu(snapshot.showInitialMenu ?? false);
      setCurrentStepIndex(
        snapshot.step <= hintStep ? 0 : (snapshot.currentStepIndex ?? 0)
      );
      setShowBegripvraag(snapshot.showBegripvraag ?? false);
      setIsZelfUitwerkenMode(snapshot.isZelfUitwerkenMode ?? false);
      setShowInitialMobileMenu(snapshot.showInitialMobileMenu ?? false);
      setSideMenuExpanded(snapshot.sideMenuExpanded ?? false);
      if (
        snapshot.inlineTheorieId !== undefined &&
        inlineTheorie?.id !== snapshot.inlineTheorieId
      ) {
        void handleInlineTheorieClick(snapshot.inlineTheorieId);
      } else if (snapshot.inlineTheorieId === undefined) {
        closeInlineTheorie();
      }
      closeTheory(false);
      setCurrentTourStep(snapshot.step);
      setCursorTarget(snapshot.cursorTarget);
      lastAppliedTikTokTourUrlRef.current = urlTikTokTourHistoryPath;
    } else {
      setShowHint(step <= hintStep);
      setShowInitialMenu(false);
      setCurrentStepIndex(0);
      setShowBegripvraag(false);
      setIsZelfUitwerkenMode(false);
      setShowInitialMobileMenu(false);
      closeInlineTheorie();
      closeTheory(false);
      setCurrentTourStep(step);
      setCursorTarget(target);
      if (urlTikTokTourHistoryPath) {
        lastAppliedTikTokTourUrlRef.current = urlTikTokTourHistoryPath;
      }
    }
    setIsTourRunning(true);
    setIsTourComplete(false);
  }, [
    closeInlineTheorie,
    closeTheory,
    forcedTikTokTourKey,
    getCursorForStep,
    handleInlineTheorieClick,
    hintStep,
    inlineTheorie?.id,
    isForcedTikTokTour,
    setCurrentStepIndex,
    setIsZelfUitwerkenMode,
    setShowBegripvraag,
    setShowHint,
    setShowInitialMenu,
    setShowInitialMobileMenu,
    setSideMenuExpanded,
    urlTikTokTourHistoryPath,
    urlTikTokTourStep,
    urlTikTokTourTarget
  ]);

  useEffect(() => {
    if (!isForcedTikTokTour || !isTourRunning || currentTourStep === null) {
      return;
    }
    let suppressedHistoryStep: TourStep | null = null;
    try {
      suppressedHistoryStep = parseTourStep(
        sessionStorage.getItem(TIKTOK_TOUR_SUPPRESS_HISTORY_STEP_KEY)
      );
    } catch {}
    if (suppressedHistoryStep !== null) {
      if (currentTourStep <= suppressedHistoryStep) {
        return;
      }
      try {
        sessionStorage.removeItem(TIKTOK_TOUR_SUPPRESS_HISTORY_STEP_KEY);
      } catch {}
    }
    if (isRestoringTikTokTourUrlRef.current) {
      if (urlTikTokTourStep === null || currentTourStep === urlTikTokTourStep) {
        isRestoringTikTokTourUrlRef.current = false;
      }
      return;
    }
    const liveTikTokTourStep =
      typeof window !== "undefined"
        ? parseTourStep(
            new URLSearchParams(window.location.search).get(
              TIKTOK_TOUR_STEP_PARAM
            )
          )
        : urlTikTokTourStep;
    if (liveTikTokTourStep !== null && currentTourStep < liveTikTokTourStep) {
      return;
    }
    const isLeavingHintStep = currentTourStep === hintStep && !showHint;
    const isLeavingStappenplanStep =
      currentTourStep === stappenplanStep &&
      stepIndexAtStap3Ref.current !== null &&
      currentStepIndex > stepIndexAtStap3Ref.current;
    const isLeavingTheoryStep =
      currentTourStep === theoryStep &&
      cursorTarget === "volgende-stap" &&
      stepIndexAtTheory !== null &&
      currentStepIndex > stepIndexAtTheory;
    const isClosingTheoryStep =
      currentTourStep === theoryStep &&
      cursorTarget === "volgende-stap" &&
      !theoryIsOpen;
    if (isLeavingHintStep || isLeavingStappenplanStep || isLeavingTheoryStep) {
      return;
    }
    if (isClosingTheoryStep) return;

    const defaultTarget = getCursorForStep(currentTourStep);
    const targetForUrl =
      cursorTarget && cursorTarget !== defaultTarget ? cursorTarget : null;
    const historyPath = getTikTokTourHistoryPath(
      pathname,
      searchParamsForHelpers,
      currentTourStep,
      targetForUrl
    );
    writeTikTokTourSnapshot(historyPath, {
      step: currentTourStep,
      cursorTarget,
      pathname: pathname ?? undefined,
      currentStepIndex,
      showHint,
      showInitialMenu,
      showBegripvraag,
      isZelfUitwerkenMode,
      showInitialMobileMenu,
      sideMenuExpanded,
      inlineTheorieId: inlineTheorie?.id
    });

    const currentPath = `${pathname ?? ""}${
      searchParamsString ? `?${searchParamsString}` : ""
    }`;
    if (currentPath === historyPath) {
      lastAppliedTikTokTourUrlRef.current = historyPath;
      return;
    }
    if (lastAppliedTikTokTourUrlRef.current === historyPath) return;

    lastAppliedTikTokTourUrlRef.current = historyPath;
    selfPushedTikTokTourUrlRef.current = historyPath;
    router.push(historyPath, { scroll: false });
  }, [
    currentStepIndex,
    currentTourStep,
    cursorTarget,
    getCursorForStep,
    hintStep,
    inlineTheorie?.id,
    isForcedTikTokTour,
    isTourRunning,
    isZelfUitwerkenMode,
    pathname,
    router,
    searchParamsForHelpers,
    searchParamsString,
    showBegripvraag,
    showHint,
    showInitialMenu,
    showInitialMobileMenu,
    sideMenuExpanded,
    stepIndexAtTheory,
    stappenplanStep,
    theoryStep,
    theoryIsOpen,
    urlTikTokTourStep
  ]);

  // ─── Start tour ───

  useEffect(() => {
    if (!currentSubvraag || !isOnVraagPage) return;
    if (shareToken) return;
    // Don't start tour while tablet rotate prompt is showing — wait for landscape
    if (isTabletPortrait) return;
    // Demo is available to anonymous users, but we still need the initial auth
    // check to finish before deciding. Otherwise logged-in users with a saved
    // backend dismissal can see the anonymous demo tour flash on a clean device.
    if (isDemo && userData === null && !isForcedTikTokTour) return;

    const effectiveTourStatus = getEffectiveTourStatus(userData);

    // TikTok invite is an explicit campaign flow, so it should start every
    // time that URL is opened. Regular demo/onboarding remains governed by
    // the global completed/skipped status.
    const shouldStart = isForcedTikTokTour
      ? true
      : isDemo
        ? !isDismissedTourStatus(effectiveTourStatus)
        : layoutMode === "web" &&
          !isMobile &&
          isLoggedIn &&
          showGuidedExperience &&
          !isDismissedTourStatus(effectiveTourStatus);

    if (shouldStart && !isTourRunning && !tourSkippedRef.current) {
      if (isForcedTikTokTour) {
        // Start recording before the mobile campaign tour intro renders.
        try {
          posthog.startSessionRecording(true);
        } catch {}
      }

      // Reset stappenplan state so tour starts clean
      setShowHint(true);
      setShowInitialMenu(false);
      setCurrentStepIndex(0);
      setShowBegripvraag(false);
      closeInlineTheorie();
      closeTheory(false);
      tourCompletionCapturedRef.current = false;
      restoredOutroRef.current = false;
      subscribeReturnPendingRef.current = false;

      setCurrentTourStep(0);
      setCursorTarget(null);
      setTheoryBridgeRemainingClicks(null);
      setIsTourRunning(true);
      setIsTourComplete(false);
    }
  }, [
    showGuidedExperience,
    currentSubvraag,
    isOnVraagPage,
    isDemo,
    isForcedTikTokTour,
    isMobile,
    isTourRunning,
    shareToken,
    setSideMenuExpanded,
    isTabletPortrait,
    isLoggedIn,
    layoutMode,
    userData?.tour_status
  ]);

  useEffect(() => {
    if (isForcedTikTokTour && isTourRunning) {
      setIsTourComplete(false);
      return;
    }
    setIsTourComplete(isDismissedTourStatus(getEffectiveTourStatus(userData)));
  }, [isForcedTikTokTour, isTourRunning, userData?.tour_status]);

  useEffect(() => {
    if (!isTourRunning) return;
    if (isForcedTikTokTour) return;
    if (!isDismissedTourStatus(userData?.tour_status)) return;
    if (restoredOutroRef.current && currentTourStep === outroStep) return;
    if (subscribeReturnPendingRef.current && currentTourStep === outroStep)
      return;

    setCurrentTourStep(null);
    setCursorTarget(null);
    setIsTourRunning(false);
  }, [
    currentTourStep,
    isForcedTikTokTour,
    isTourRunning,
    outroStep,
    userData?.tour_status
  ]);

  useEffect(() => {
    if (isForcedTikTokTour) return;
    if (
      !currentSubvraag ||
      stepMapping.length === 0 ||
      !isOnVraagPage ||
      isTourRunning
    )
      return;
    if (shareToken || isTabletPortrait) return;

    let snapshot: TourReturnSnapshot | null = null;

    try {
      if (
        sessionStorage.getItem(TOUR_RETURN_TO_OUTRO_KEY) !== "true" ||
        readLocalTourStatus() !== "completed"
      ) {
        return;
      }
      const rawSnapshot = sessionStorage.getItem(TOUR_RETURN_SNAPSHOT_KEY);
      if (!rawSnapshot) return;
      const historyToken = getTourReturnHistoryToken();
      if (!historyToken) {
        clearTourReturnState();
        return;
      }
      snapshot = JSON.parse(rawSnapshot) as TourReturnSnapshot;
      if (snapshot.pathname && snapshot.pathname !== pathname) return;
      if (snapshot.token !== historyToken) {
        clearTourReturnState();
        return;
      }
    } catch {
      return;
    }

    const restoreTimer = window.setTimeout(() => {
      if (snapshot) {
        setCurrentStepIndex(
          Math.min(
            Math.max(snapshot.currentStepIndex ?? 0, 0),
            Math.max(stepMapping.length - 1, 0)
          )
        );
        setShowHint(snapshot.showHint ?? false);
        setShowInitialMenu(snapshot.showInitialMenu ?? false);
        setShowBegripvraag(snapshot.showBegripvraag ?? false);
        setIsZelfUitwerkenMode(snapshot.isZelfUitwerkenMode ?? false);
        setShowInitialMobileMenu(snapshot.showInitialMobileMenu ?? false);
        setSideMenuExpanded(snapshot.sideMenuExpanded ?? false);
      }

      tourStartTimeRef.current = Date.now();
      tourCompletionCapturedRef.current = true;
      restoredOutroRef.current = true;
      setCurrentTourStep(outroStep);
      setCursorTarget(null);
      setIsTourRunning(true);
      setIsTourComplete(true);
      window.setTimeout(clearTourReturnState, 0);
    }, 0);

    return () => window.clearTimeout(restoreTimer);
  }, [
    currentSubvraag,
    isOnVraagPage,
    isDemo,
    isForcedTikTokTour,
    outroStep,
    pathname,
    isTourRunning,
    shareToken,
    isTabletPortrait,
    setCurrentStepIndex,
    setShowHint,
    setShowInitialMenu,
    setShowBegripvraag,
    setIsZelfUitwerkenMode,
    setShowInitialMobileMenu,
    setSideMenuExpanded,
    stepMapping.length
  ]);

  // Cancel tour if user navigates away from vraag page
  useEffect(() => {
    if (!isOnVraagPage) {
      // If tour was active and not already terminated, fire navigated-away
      if (
        isTourRunning &&
        currentTourStep !== null &&
        !tourSkippedRef.current
      ) {
        const stepDuration =
          stepEnteredAtRef.current !== null
            ? Math.round((Date.now() - stepEnteredAtRef.current) / 1000)
            : null;
        const totalDuration =
          tourStartTimeRef.current !== null
            ? Math.round((Date.now() - tourStartTimeRef.current) / 1000)
            : null;
        posthog.capture("tour_navigated_away", {
          at_step: currentTourStep,
          at_step_name: stepName[currentTourStep],
          step_duration_sec: stepDuration,
          total_duration_sec: totalDuration,
          flow
        });
      }
      setCurrentTourStep(null);
      setCursorTarget(null);
      setTheoryBridgeRemainingClicks(null);
      setIsTourRunning(false);
      restoredOutroRef.current = false;
      subscribeReturnPendingRef.current = false;
      tourSkippedRef.current = false; // Reset so tour can start on next visit
      tourStartTimeRef.current = null;
      lastTrackedStepRef.current = null;
      stepEnteredAtRef.current = null;
      previousStepRef.current = null;
    }
    // We intentionally only depend on isOnVraagPage so this fires once per
    // navigation transition, not on every tour-state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnVraagPage]);

  // ─── Tour lifecycle analytics ───
  useEffect(() => {
    if (currentTourStep === null) {
      lastTrackedStepRef.current = null;
      return;
    }
    // tour_started: first entry into the tour this visit
    if (tourStartTimeRef.current === null) {
      tourStartTimeRef.current = Date.now();
      let entryFlow: string | null = null;
      try {
        entryFlow = sessionStorage.getItem("wiswiz_entry_flow");
      } catch {}
      const audience = isParentAudience ? "parent" : "student";
      posthog.register({
        entry_flow: entryFlow ?? flow,
        audience
      });
      posthog.capture("tour_started", {
        flow,
        entry_flow: entryFlow ?? flow,
        audience
      });
    }
    // tour_step_viewed: on every change to a non-null step.
    // Includes prev_step and prev_step_duration_sec so the time spent in the
    // previous step is captured on every transition — including auto-advances
    // and auto-skips that don't fire tour_step_advanced.
    if (lastTrackedStepRef.current !== currentTourStep) {
      const now = Date.now();
      const prevStep = previousStepRef.current;
      const prevStepDuration =
        stepEnteredAtRef.current !== null
          ? Math.round((now - stepEnteredAtRef.current) / 1000)
          : null;
      lastTrackedStepRef.current = currentTourStep;
      previousStepRef.current = currentTourStep;
      stepEnteredAtRef.current = now;
      posthog.capture("tour_step_viewed", {
        step: currentTourStep,
        step_name: stepName[currentTourStep],
        flow,
        prev_step: prevStep,
        prev_step_name: prevStep !== null ? stepName[prevStep] : null,
        prev_step_duration_sec: prevStepDuration
      });
    }
  }, [currentTourStep, flow, isParentAudience]);

  // ─── Tab visibility / window focus → tour_paused / tour_resumed ───
  useEffect(() => {
    if (!isTourRunning) return;
    let pausedAt: number | null = null;

    const onVisibilityChange = () => {
      if (document.hidden) {
        if (pausedAt !== null) return;
        pausedAt = Date.now();
        posthog.capture("tour_paused", {
          step: currentTourStep,
          step_name:
            currentTourStep !== null ? stepName[currentTourStep] : null,
          flow
        });
      } else if (pausedAt !== null) {
        const pauseDurationSec = Math.round((Date.now() - pausedAt) / 1000);
        pausedAt = null;
        posthog.capture("tour_resumed", {
          step: currentTourStep,
          step_name:
            currentTourStep !== null ? stepName[currentTourStep] : null,
          pause_duration_sec: pauseDurationSec,
          flow
        });
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [isTourRunning, currentTourStep, flow]);

  // ─── TikTok mobile interaction guard: only tour UI and the current target are pressable ───
  useEffect(() => {
    if (!isForcedTikTokTour || !isTourRunning || currentTourStep === null)
      return;
    const isAllowedTourInteraction = (target: Element) => {
      if (target.closest('[data-tour-ui="true"]')) return true;
      if (!cursorTarget) return false;
      return target.closest(`[data-tour-target='${cursorTarget}']`) !== null;
    };
    const captureOffTargetClick = (target: Element) => {
      const tag = (target as HTMLElement).tagName?.toLowerCase() ?? "?";
      const isButton = tag === "button" || target.closest("button") !== null;
      const isLink = tag === "a" || target.closest("a") !== null;
      posthog.capture("tour_off_target_click", {
        step: currentTourStep,
        step_name: stepName[currentTourStep],
        expected_target: cursorTarget,
        tag,
        was_button: isButton,
        was_link: isLink,
        flow
      });
    };
    const blockEvent = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };
    const onPress = (e: Event) => {
      const target = e.target as Element | null;
      if (!target || !(target instanceof Element)) return;
      if (isAllowedTourInteraction(target)) return;
      blockEvent(e);
    };
    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target || !(target instanceof Element)) return;

      if (
        cursorTarget &&
        target.closest(`[data-tour-target='${cursorTarget}']`)
      ) {
        posthog.capture("tour_target_clicked", {
          step: currentTourStep,
          step_name: stepName[currentTourStep],
          target: cursorTarget,
          flow
        });
        return;
      }

      // Ignore non-target clicks on tour UI itself (overlay, tip, cursor, intro card)
      if (target.closest('[data-tour-ui="true"]')) return;

      // Off-target click — interesting signal: user clicked something other than
      // the cursor target during an active tour step.
      captureOffTargetClick(target);
      blockEvent(e);
    };
    document.addEventListener("pointerdown", onPress, true);
    document.addEventListener("touchstart", onPress, {
      capture: true,
      passive: false
    });
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("pointerdown", onPress, true);
      document.removeEventListener("touchstart", onPress, true);
      document.removeEventListener("click", onClick, true);
    };
  }, [
    isForcedTikTokTour,
    isTourRunning,
    currentTourStep,
    cursorTarget,
    flow,
    stepName
  ]);

  // Keep sidebar collapsed during active tour steps (1–5).
  // closeTheory() defaults to restoreSidebar=true which expands the sidebar;
  // that must not happen while the sidebar is dimmed & pointer-events-none.
  useEffect(() => {
    if (
      isTourRunning &&
      currentTourStep !== null &&
      currentTourStep > 0 &&
      currentTourStep < outroStep &&
      sideMenuExpanded
    ) {
      setSideMenuExpanded(false);
    }
  }, [
    isTourRunning,
    currentTourStep,
    outroStep,
    sideMenuExpanded,
    setSideMenuExpanded
  ]);

  // The vraag page has its own "fresh subvraag" reset, and the normal product
  // flow now starts with a mode selector. Keep the guided tour on the surface
  // that belongs to the active tour step if those resets race with the tour.
  useEffect(() => {
    if (
      isForcedTikTokTour ||
      !isTourRunning ||
      currentTourStep === null ||
      currentTourStep === outroStep
    ) {
      return;
    }

    const introMenuWasVisible = showInitialMenu || showInitialMobileMenu;

    if (showInitialMenu) setShowInitialMenu(false);
    if (showInitialMobileMenu) setShowInitialMobileMenu(false);
    if (isZelfUitwerkenMode) setIsZelfUitwerkenMode(false);
    if (showBegripvraag) setShowBegripvraag(false);

    const shouldRestoreHint =
      currentTourStep > 0 &&
      currentTourStep <= hintStep &&
      !showHint &&
      (currentTourStep < hintStep || introMenuWasVisible);

    if (shouldRestoreHint) {
      setShowHint(true);
      return;
    }

    if (currentTourStep >= stappenplanStep && showHint) {
      void startStappenplan();
    }
  }, [
    currentTourStep,
    hintStep,
    isForcedTikTokTour,
    isTourRunning,
    isZelfUitwerkenMode,
    outroStep,
    showBegripvraag,
    showHint,
    showInitialMenu,
    showInitialMobileMenu,
    setIsZelfUitwerkenMode,
    setShowBegripvraag,
    setShowHint,
    setShowInitialMenu,
    setShowInitialMobileMenu,
    startStappenplan,
    stappenplanStep
  ]);

  // ─── Helper: go to a step and set its cursor ───

  const goToStep = useCallback(
    (step: TourStep) => {
      restoredTikTokTourStepRef.current = null;
      if (delayedCursorTimerRef.current) {
        clearTimeout(delayedCursorTimerRef.current);
        delayedCursorTimerRef.current = null;
      }
      setTheoryBridgeRemainingClicks(null);

      // If jumping past the hint step, auto-start stappenplan in the same
      // event loop tick so there's no flash of the hint display.
      if (step >= stappenplanStep && skippedSteps.has(hintStep) && showHint) {
        startStappenplan();
      }
      setCurrentTourStep(step);
      const cursor =
        step === 1 && !isDemo
          ? "tour-primary-action"
          : stepCursor[step] || null;

      setCursorTarget(cursor);
    },
    [
      hintStep,
      isDemo,
      skippedSteps,
      showHint,
      stappenplanStep,
      startStappenplan,
      stepCursor
    ]
  );

  // ─── Safety: if skippedSteps changes and we're on a now-skipped step, advance ───

  useEffect(() => {
    if (
      currentTourStep !== null &&
      currentTourStep > 0 &&
      currentTourStep < outroStep &&
      skippedSteps.has(currentTourStep)
    ) {
      goToStep(nextActiveStep(currentTourStep));
    }
  }, [currentTourStep, outroStep, skippedSteps, goToStep, nextActiveStep]);

  // ─── Auto-detect: user clicks the element the cursor points to ───

  // Hint step → next: user clicked "Begin stappenplan" (showHint becomes false)
  const [stepIndexAtStap3, setStepIndexAtStap3State] = useState<number | null>(
    null
  );
  const setStepIndexAtStap3 = useCallback((value: number | null) => {
    stepIndexAtStap3Ref.current = value;
    setStepIndexAtStap3State(value);
  }, []);

  useEffect(() => {
    if (currentTourStep === hintStep && !showHint) {
      setStepIndexAtStap3(currentStepIndex);
      goToStep(nextActiveStep(currentTourStep));
    }
  }, [
    showHint,
    currentTourStep,
    hintStep,
    goToStep,
    currentStepIndex,
    nextActiveStep,
    setStepIndexAtStap3
  ]);

  // When entering the stappenplan step, record the starting step index
  useEffect(() => {
    const isRestoredStappenplanStep =
      isForcedTikTokTour &&
      restoredTikTokTourStepRef.current === stappenplanStep;
    if (
      currentTourStep === stappenplanStep &&
      (stepIndexAtStap3 === null || isRestoredStappenplanStep)
    ) {
      setStepIndexAtStap3(currentStepIndex);
    }
  }, [
    currentTourStep,
    currentStepIndex,
    isForcedTikTokTour,
    setStepIndexAtStap3,
    stepIndexAtStap3,
    stappenplanStep
  ]);

  // Stappenplan step → theory step: after user clicks "Volgende stap", check for theory.
  useEffect(() => {
    if (
      currentTourStep !== stappenplanStep ||
      stepIndexAtStap3 === null ||
      currentStepIndex <= stepIndexAtStap3
    )
      return;

    if (
      isForcedTikTokTour &&
      restoredTikTokTourStepRef.current === stappenplanStep
    ) {
      restoredTikTokTourStepRef.current = null;
    }

    setCursorTarget(null);

    // If theory step is skipped entirely, go straight to bijlesbot
    if (skippedSteps.has(theoryStep)) {
      if (!isTikTokTour && firstVisibleDesktopTheoryStepIndex !== null) {
        const expectedTheoryCheckIndex = stepIndexAtStap3 + 1;
        const extraClicksToTheory =
          firstVisibleDesktopTheoryStepIndex - expectedTheoryCheckIndex;
        posthog.capture("tour_theory_step_skipped", {
          reason:
            firstVisibleDesktopTheoryStepIndex < expectedTheoryCheckIndex
              ? "theory_before_tour_check"
              : "theory_too_far",
          first_theory_step_index: firstVisibleDesktopTheoryStepIndex,
          current_step_index: currentStepIndex,
          extra_clicks_to_theory: extraClicksToTheory,
          max_extra_clicks: MAX_THEORY_BRIDGE_EXTRA_CLICKS,
          flow
        });
        setTheoryBridgeRemainingClicks(null);
      }
      goToStep(nextActiveStep(stappenplanStep));
      return;
    }

    if (!isTikTokTour && firstVisibleDesktopTheoryStepIndex !== null) {
      const expectedTheoryCheckIndex = stepIndexAtStap3 + 1;
      const extraClicksToTheory =
        firstVisibleDesktopTheoryStepIndex - expectedTheoryCheckIndex;

      if (firstVisibleDesktopTheoryStepIndex < currentStepIndex) {
        posthog.capture("tour_theory_step_skipped", {
          reason: "theory_already_passed",
          first_theory_step_index: firstVisibleDesktopTheoryStepIndex,
          current_step_index: currentStepIndex,
          flow
        });
        setTheoryBridgeRemainingClicks(null);
        goToStep(nextActiveStep(theoryStep));
        return;
      }

      if (extraClicksToTheory > MAX_THEORY_BRIDGE_EXTRA_CLICKS) {
        posthog.capture("tour_theory_step_skipped", {
          reason: "theory_too_far",
          first_theory_step_index: firstVisibleDesktopTheoryStepIndex,
          current_step_index: currentStepIndex,
          extra_clicks_to_theory: extraClicksToTheory,
          max_extra_clicks: MAX_THEORY_BRIDGE_EXTRA_CLICKS,
          flow
        });
        setTheoryBridgeRemainingClicks(null);
        goToStep(nextActiveStep(theoryStep));
        return;
      }

      if (firstVisibleDesktopTheoryStepIndex > currentStepIndex) {
        setTheoryBridgeRemainingClicks(
          firstVisibleDesktopTheoryStepIndex - currentStepIndex
        );
        setCursorTarget("volgende-stap");
        return;
      }
    }

    setTheoryBridgeRemainingClicks(null);

    // Poll for the inline theorie link element
    let attempts = 0;
    const check = setInterval(() => {
      attempts++;
      const pill = document.querySelector(
        `[data-tour-target='${theoryTarget}']`
      );
      if (pill) {
        clearInterval(check);
        posthog.capture("tour_step_target_resolved", {
          step: stappenplanStep,
          step_name: stepName[stappenplanStep],
          target: theoryTarget,
          attempts,
          flow
        });
        goToStep(theoryStep);
      } else if (attempts >= 10) {
        clearInterval(check);
        posthog.capture("tour_step_target_unresolved", {
          step: stappenplanStep,
          step_name: stepName[stappenplanStep],
          target: theoryTarget,
          attempts,
          flow
        });
        // Theory exists somewhere but not on this step
        if (isTikTokTour && currentStepIndex < stepMapping.length - 1) {
          setCursorTarget("volgende-stap");
        } else {
          // Reached last step without finding theory — skip to next
          goToStep(nextActiveStep(theoryStep));
        }
      }
    }, 100);
    return () => clearInterval(check);
  }, [
    currentStepIndex,
    currentTourStep,
    firstVisibleDesktopTheoryStepIndex,
    flow,
    goToStep,
    isForcedTikTokTour,
    isTikTokTour,
    stepIndexAtStap3,
    skippedSteps,
    nextActiveStep,
    stepMapping,
    stepName,
    stappenplanStep,
    theoryStep,
    theoryTarget
  ]);

  useEffect(() => {
    if (
      isForcedTikTokTour &&
      restoredTikTokTourStepRef.current === stappenplanStep &&
      currentTourStep === stappenplanStep &&
      stepIndexAtStap3 === currentStepIndex
    ) {
      restoredTikTokTourStepRef.current = null;
    }
  }, [
    currentStepIndex,
    currentTourStep,
    isForcedTikTokTour,
    stepIndexAtStap3,
    stappenplanStep
  ]);

  // Theory step: record the step index when we enter this tour step
  const [stepIndexAtStep4, setStepIndexAtStep4State] = useState<number | null>(
    null
  );
  const setStepIndexAtStep4 = useCallback((value: number | null) => {
    setStepIndexAtStep4State(value);
  }, []);
  useEffect(() => {
    const isRestoredTheoryStep =
      isForcedTikTokTour && restoredTikTokTourStepRef.current === theoryStep;
    if (
      currentTourStep === theoryStep &&
      (stepIndexAtStep4 === null || isRestoredTheoryStep)
    ) {
      setStepIndexAtStep4(currentStepIndex);
    }
    if (currentTourStep !== theoryStep) {
      setStepIndexAtStep4(null);
    }
  }, [
    currentTourStep,
    currentStepIndex,
    isForcedTikTokTour,
    setStepIndexAtStep4,
    stepIndexAtStep4,
    theoryStep
  ]);

  // Theory step: user opened theory -> cursor to "Volgende stap"
  useEffect(() => {
    if (currentTourStep === theoryStep && theoryIsOpen) {
      if (delayedCursorTimerRef.current) {
        clearTimeout(delayedCursorTimerRef.current);
        delayedCursorTimerRef.current = null;
      }
      setStepIndexAtTheory(currentStepIndex);
      setCursorTarget("volgende-stap");
    }
  }, [theoryIsOpen, currentTourStep, currentStepIndex, theoryStep]);

  // Theory step safety: if no theory target appears within 3s, skip to next step
  useEffect(() => {
    if (currentTourStep === theoryStep && !theoryIsOpen) {
      const timer = setTimeout(() => {
        const pill = document.querySelector(
          `[data-tour-target='${theoryTarget}']`
        );
        if (!pill) {
          posthog.capture("tour_step_target_unresolved", {
            step: theoryStep,
            step_name: stepName[theoryStep],
            target: theoryTarget,
            reason: "safety_timeout_3s",
            flow
          });
          goToStep(nextActiveStep(theoryStep));
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [
    currentTourStep,
    theoryIsOpen,
    goToStep,
    nextActiveStep,
    flow,
    stepName,
    theoryStep,
    theoryTarget
  ]);

  useEffect(() => {
    if (!isForcedTikTokTour || currentTourStep !== theoryStep || theoryIsOpen) {
      return;
    }

    let suppressedHistoryStep: TourStep | null = null;
    try {
      suppressedHistoryStep = parseTourStep(
        sessionStorage.getItem(TIKTOK_TOUR_SUPPRESS_HISTORY_STEP_KEY)
      );
    } catch {}

    if (suppressedHistoryStep !== theoryStep && stepIndexAtTheory === null) {
      return;
    }

    goToStep(nextActiveStep(theoryStep));
    setStepIndexAtTheory(null);
  }, [
    currentTourStep,
    goToStep,
    isForcedTikTokTour,
    nextActiveStep,
    stepIndexAtTheory,
    theoryIsOpen,
    theoryStep
  ]);

  // Theory step → bijlesbot: user clicked "Volgende stap"
  useEffect(() => {
    if (currentTourStep !== theoryStep || stepIndexAtStep4 === null) return;
    if (currentStepIndex <= stepIndexAtStep4) return;
    if (
      isForcedTikTokTour &&
      restoredTikTokTourStepRef.current === theoryStep
    ) {
      restoredTikTokTourStepRef.current = null;
    }

    // User advanced the stappenplan — move to next tour step
    closeInlineTheorie();
    closeTheory(false);
    goToStep(nextActiveStep(theoryStep));
    setStepIndexAtTheory(null);
  }, [
    currentStepIndex,
    currentTourStep,
    isForcedTikTokTour,
    stepIndexAtStep4,
    closeInlineTheorie,
    closeTheory,
    goToStep,
    nextActiveStep,
    theoryStep
  ]);

  useEffect(() => {
    if (
      isForcedTikTokTour &&
      restoredTikTokTourStepRef.current === theoryStep &&
      currentTourStep === theoryStep &&
      stepIndexAtStep4 === currentStepIndex
    ) {
      restoredTikTokTourStepRef.current = null;
    }
  }, [
    currentStepIndex,
    currentTourStep,
    isForcedTikTokTour,
    stepIndexAtStep4,
    theoryStep
  ]);

  // Step 6 (bijlesbot): no auto-advance — user clicks "Afronden" via advanceTour

  // ─── Tour controls ───

  // Shared cleanup. Does NOT fire any analytics — each caller is responsible
  // for its own event (tour_completed / tour_skipped). Previously skipTour
  // called completeTour which caused every skip to also fire tour_completed.
  const persistTourStatus = useCallback(
    async (status: TourStatus) => {
      writeLocalTourStatus(status, { pendingSync: !isLoggedIn });
      setTourStatusOnUserData(status);
      if (isLoggedIn) {
        try {
          await api.updateTourStatus(status);
          writeLocalTourStatus(status, { pendingSync: false });
        } catch {
          writeLocalTourStatus(status, { pendingSync: true });
        }
      }
    },
    [api, isLoggedIn, setTourStatusOnUserData]
  );

  const endTour = useCallback(
    async (status: TourStatus) => {
      tourSkippedRef.current = true;
      tourStartTimeRef.current = null;
      stepEnteredAtRef.current = null;
      previousStepRef.current = null;
      setCurrentTourStep(null);
      setCursorTarget(null);
      setIsTourRunning(false);
      setIsTourComplete(true);
      restoredOutroRef.current = false;
      subscribeReturnPendingRef.current = false;
      clearTourReturnState();
      setSideMenuExpanded(true);
      setShowGuidedExperience(false);
      if (!isForcedTikTokTour) {
        await persistTourStatus(status);
      }
    },
    [
      isForcedTikTokTour,
      persistTourStatus,
      setSideMenuExpanded,
      setShowGuidedExperience
    ]
  );

  const captureTourCompleted = useCallback(() => {
    if (tourCompletionCapturedRef.current) return;
    tourCompletionCapturedRef.current = true;
    const duration_sec =
      tourStartTimeRef.current !== null
        ? Math.round((Date.now() - tourStartTimeRef.current) / 1000)
        : null;
    const finalStepDuration =
      stepEnteredAtRef.current !== null
        ? Math.round((Date.now() - stepEnteredAtRef.current) / 1000)
        : null;
    posthog.capture("tour_completed", {
      flow,
      duration_sec,
      final_step: currentTourStep,
      final_step_name:
        currentTourStep !== null ? stepName[currentTourStep] : null,
      final_step_duration_sec: finalStepDuration
    });
  }, [flow, currentTourStep]);

  const dismissBookNoticeAfterDemoTour = useCallback(() => {
    if (!isDemo) return;
    try {
      window.localStorage.setItem(OPGAVE_IN_BOEK_NOTICE_STORAGE_KEY, "true");
    } catch {}

    setUserPreferencesOnUserData({
      [OPGAVE_IN_BOEK_NOTICE_PREFERENCE_KEY]: true
    });

    if (userData) {
      api
        .updateUserPreferences({
          [OPGAVE_IN_BOEK_NOTICE_PREFERENCE_KEY]: true
        })
        .catch(() => {
          // Local dismissal is enough to avoid repeating the book prompt.
        });
    }
  }, [api, isDemo, setUserPreferencesOnUserData, userData]);

  const completeTourBeforeSubscribe = useCallback(() => {
    captureTourCompleted();
    dismissBookNoticeAfterDemoTour();
    tourSkippedRef.current = true;
    subscribeReturnPendingRef.current = true;
    setIsTourComplete(true);
    setShowGuidedExperience(false);
    const token = createTourReturnToken();
    setTourReturnHistoryToken(token);
    sessionStorage.setItem(TOUR_RETURN_TO_OUTRO_KEY, "true");
    sessionStorage.setItem(
      TOUR_RETURN_SNAPSHOT_KEY,
      JSON.stringify({
        token,
        pathname,
        currentStepIndex,
        showHint,
        showInitialMenu,
        showBegripvraag,
        isZelfUitwerkenMode,
        showInitialMobileMenu,
        sideMenuExpanded
      })
    );
    void persistTourStatus("completed");
  }, [
    captureTourCompleted,
    currentStepIndex,
    dismissBookNoticeAfterDemoTour,
    isZelfUitwerkenMode,
    persistTourStatus,
    showBegripvraag,
    showHint,
    showInitialMenu,
    showInitialMobileMenu,
    pathname,
    sideMenuExpanded,
    setShowGuidedExperience
  ]);

  const completeTour = useCallback(async () => {
    captureTourCompleted();
    dismissBookNoticeAfterDemoTour();
    await endTour("completed");
  }, [captureTourCompleted, dismissBookNoticeAfterDemoTour, endTour]);

  const advanceTour = useCallback(async () => {
    if (currentTourStep === null) return;
    const fromStepDuration =
      stepEnteredAtRef.current !== null
        ? Math.round((Date.now() - stepEnteredAtRef.current) / 1000)
        : null;

    if (currentTourStep === outroStep) {
      posthog.capture("tour_step_advanced", {
        from_step: outroStep,
        from_step_name: stepName[outroStep],
        to_step: null,
        from_step_duration_sec: fromStepDuration,
        flow
      });
      await completeTour();
      return;
    }
    // Regular mobile demo shows intro only. TikTok invite uses the full
    // mobile-guided tour.
    if (isMobile && !isTikTokTour) {
      posthog.capture("tour_step_advanced", {
        from_step: currentTourStep,
        from_step_name: stepName[currentTourStep],
        to_step: null,
        from_step_duration_sec: fromStepDuration,
        flow
      });
      await completeTour();
      return;
    }
    // Close sidebar when guidance actually starts (leaving intro)
    if (currentTourStep === 0) {
      setSideMenuExpanded(false);
    }
    const next = nextActiveStep(currentTourStep);
    // Emit a tour_step_auto_skipped event for every step that gets hopped over
    // between from_step and to_step due to the skippedSteps mechanism. Lets
    // dashboards separate "user advanced normally" from "tour auto-jumped
    // because this question had no hint / single step / no theorie".
    for (
      let s = (currentTourStep + 1) as TourStep;
      s < next;
      s = (s + 1) as TourStep
    ) {
      if (skippedSteps.has(s)) {
        posthog.capture("tour_step_auto_skipped", {
          skipped_step: s,
          skipped_step_name: stepName[s],
          from_step: currentTourStep,
          to_step: next,
          flow
        });
      }
    }

    posthog.capture("tour_step_advanced", {
      from_step: currentTourStep,
      from_step_name: stepName[currentTourStep],
      to_step: next,
      to_step_name: stepName[next],
      from_step_duration_sec: fromStepDuration,
      flow
    });
    goToStep(next);
  }, [
    currentTourStep,
    goToStep,
    completeTour,
    nextActiveStep,
    isMobile,
    isTikTokTour,
    flow,
    setSideMenuExpanded,
    skippedSteps
  ]);

  const skipTour = useCallback(async () => {
    const finalStepDuration =
      stepEnteredAtRef.current !== null
        ? Math.round((Date.now() - stepEnteredAtRef.current) / 1000)
        : null;
    const totalDuration =
      tourStartTimeRef.current !== null
        ? Math.round((Date.now() - tourStartTimeRef.current) / 1000)
        : null;
    posthog.capture("tour_skipped", {
      at_step: currentTourStep,
      at_step_name: currentTourStep !== null ? stepName[currentTourStep] : null,
      final_step_duration_sec: finalStepDuration,
      total_duration_sec: totalDuration,
      flow
    });
    closeInlineTheorie();
    closeTheory(false);
    await endTour("skipped");
  }, [
    closeInlineTheorie,
    closeTheory,
    currentTourStep,
    endTour,
    flow
  ]);

  const restartGuided = useCallback(() => {
    tourSkippedRef.current = false;
    persistTourStatus("not_started");
    clearTourReturnState();
    setShowGuidedExperience(true);
    setShowHint(true);
    setShowInitialMenu(false);
    setCurrentStepIndex(0);
    setShowBegripvraag(false);
    closeInlineTheorie();
    closeTheory();
    setCurrentTourStep(0);
    setCursorTarget(null);
    setIsTourRunning(true);
    setIsTourComplete(false);
    tourCompletionCapturedRef.current = false;
    restoredOutroRef.current = false;
    subscribeReturnPendingRef.current = false;
    setStepIndexAtStap3(null);
    setStepIndexAtTheory(null);
  }, [
    persistTourStatus,
    setShowGuidedExperience,
    setShowHint,
    setShowInitialMenu,
    setCurrentStepIndex,
    setShowBegripvraag,
    closeInlineTheorie,
    closeTheory
  ]);

  const value = useMemo(
    () => ({
      currentTourStep,
      cursorTarget,
      theoryBridgeRemainingClicks,
      skippedSteps,
      advanceTour,
      completeTourBeforeSubscribe,
      skipTour,
      restartGuided,
      isTourRunning,
      isTourComplete,
      isDemo
    }),
    [
      currentTourStep,
      cursorTarget,
      theoryBridgeRemainingClicks,
      skippedSteps,
      advanceTour,
      completeTourBeforeSubscribe,
      skipTour,
      restartGuided,
      isTourRunning,
      isTourComplete,
      isDemo
    ]
  );

  return (
    <GuidedOnboardingContext.Provider value={value}>
      {children}
    </GuidedOnboardingContext.Provider>
  );
}

export function useOptionalGuidedOnboarding() {
  return useContext(GuidedOnboardingContext);
}
